/**
 * @file   src\config\axiosConfig.ts
 * @brief  This file is responsible for axios client configurations.
 * @date   April, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */

import axios, { AxiosRequestConfig } from 'axios';
import { getFromLocalStorage} from '../utils/helper';
import { AuthApi } from '../utils/apiUrls';
import { STATUS_498, API_TIMEOUT, API_MAXCONTENTLENGTH, API_REDIRECTS,   API_REFRESH_TOKEN_ERROR } from '../utils/constants';
import { MessageToaster } from '../utils/toastUtil';
import { ErrorMessages } from '../messages/ErrorMessages';
import Cookies from 'js-cookie';
// Craete axios instance.
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});
const toaster = new MessageToaster();

// Set axios headers.
const headers: any = {};
headers['Access-Control-Allow-Origin'] = '*';
headers['Access-Control-Allow-Headers'] = '*';
headers['Access-Control-Allow-Credentials'] = true;
headers['Access-Control-Allow-Methods'] = 'GET, POST, PUT, PATCH, DELETE';
headers['Content-Type'] = 'application/json';
headers.Accept = 'application/json';
headers['Cache-Control'] = 'no-cache';
headers['api_key'] = process.env.REACT_APP_API_KEY;

// Renew access token using refresh token.
export const renewToken = async () => {
  try {
    const token = Cookies.get('accessToken')
    // storageData ? storageData.accessToken : '';
    headers.Authorization = `Bearer ${token}`;
    headers['refresh_token'] = Cookies.get('refreshToken')
    // storageData.refreshToken;
    const { data } = await axiosInstance.post(AuthApi.REFRESH_TOKEN);
    return data;
  } catch (error: any) {
    return Promise.reject(error.message);
  }
};

// Assign default values.
axiosInstance.defaults.baseURL = process.env.REACT_APP_API_BASE_URL;
axiosInstance.defaults.headers = headers;
axiosInstance.defaults.timeout = API_TIMEOUT;
axiosInstance.defaults.maxContentLength = API_MAXCONTENTLENGTH;
axiosInstance.defaults.maxRedirects = API_REDIRECTS;
axiosInstance.defaults.withCredentials = true;

// Define the structure of a retry queue item.
interface RetryQueueItem {
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
  config: AxiosRequestConfig;
}

// Create a list to hold the request queue.
const refreshAndRetryQueue: RetryQueueItem[] = [];

// Flag to prevent multiple token refresh requests.
let isRefreshing = false;

// Handle interceptor request.
axiosInstance.interceptors.request.use(
  async (config: any) => {
    const token =  Cookies.get('accessToken')
    // storageData ? storageData.accessToken : '';

    // Modify the request.
    config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => {
    Promise.reject(error);
  },
);

// Handle interceptor response.
axiosInstance.interceptors.response.use(
  
  (response) => {
    if (response.data?.error === API_REFRESH_TOKEN_ERROR) {
      localStorage.removeItem('CARWASH_ADMIN_DATA');
      localStorage.removeItem('CARWASH_OWNER_DATA');
      const current_path = window.location.pathname
      const firstPath = current_path.split('/')[1];
      if(firstPath == "superadmin")
      {
        window.location.href = `${process.env.REACT_APP_SITE_URL || ""}${firstPath}?type=snout`; 
      }
      
      else{
        window.location.href = `${process.env.REACT_APP_SITE_URL || ""}?type=snout`; 
      } 
    }
    return response;
  },
  async (error) => {
    const originalRequest: AxiosRequestConfig = error.config;
    if(error?.code === "ERR_NETWORK")
    {
      toaster.toastError(ErrorMessages.ERR_NETWORK)
      return false
    }
    if (error.response && error.response.status === STATUS_498) {
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          // Refresh the access token
          const res = await renewToken();
          if(res?.error)
          {
            localStorage.removeItem('CARWASH_ADMIN_DATA');
            localStorage.removeItem('CARWASH_OWNER_DATA');
            const current_path = window.location.pathname
            const firstPath = current_path.split('/')[1];
            if(firstPath == "superadmin")
            {
              window.location.href = `${process.env.REACT_APP_SITE_URL || ""}${firstPath}?type=snout`; 
            }
            
            else{
              window.location.href = `${process.env.REACT_APP_SITE_URL || ""}?type=snout`; 
            }  
          }
          const newAccessToken = res?.data?.accessToken || '';
          const newRefreshToken = res?.data?.refreshToken || '';
          // Update the request headers with the new access token
          error.config.headers.Authorization = `Bearer ${newAccessToken}`;
          const storageData = getFromLocalStorage('CARWASH_ADMIN_DATA') ? getFromLocalStorage('CARWASH_ADMIN_DATA') : getFromLocalStorage('CARWASH_OWNER_DATA');
          if(storageData)
          {
            Cookies.set('accessToken',newAccessToken);
            Cookies.set('refreshToken',newRefreshToken)
            // storageData.accessToken = newAccessToken;
            // storageData.refreshToken = newRefreshToken;
            // if(getFromLocalStorage('CARWASH_ADMIN_DATA')) {
            //   localStorage.removeItem('CARWASH_ADMIN_DATA');
            //   setLocalStorage('CARWASH_ADMIN_DATA', JSON.stringify(storageData));
            // } else {
            //   localStorage.removeItem('CARWASH_OWNER_DATA');
            //   setLocalStorage('CARWASH_OWNER_DATA', JSON.stringify(storageData));
            // }
          }
          
          // Retry all requests in the queue with the new token
          refreshAndRetryQueue.forEach(({ config, resolve, reject }) => {
            axiosInstance
              .request(config)
              .then((response) => resolve(response))
              .catch((err) => reject(err));
          });

          // Clear the queue
          refreshAndRetryQueue.length = 0;

          // Retry the original request
          return axiosInstance(originalRequest);
          // eslint-disable-next-line no-useless-catch
        } catch (refreshError) {
          localStorage.removeItem('CARWASH_ADMIN_DATA');
          localStorage.removeItem('CARWASH_OWNER_DATA');
          Cookies.remove('accessToken')
          Cookies.remove('refreshToken')
          const current_path = window.location.pathname
          const firstPath = current_path.split('/')[1];
          if(firstPath == "superadmin")
            {
              window.location.href = `${process.env.REACT_APP_SITE_URL || ""}${firstPath}?type=snout`; 
            }
            
            else{
              window.location.href = `${process.env.REACT_APP_SITE_URL || ""}?type=snout`; 
            }          throw refreshError;
        } finally {
          isRefreshing = false;
        }
      }

      // Add the original request to the queue
      return new Promise<void>((resolve, reject) => {
        refreshAndRetryQueue.push({ config: originalRequest, resolve, reject });
      });
    }
    if (error.response && error.response.status === 500) {
    }


    // Return a Promise rejection if the status code is not 401
    return Promise.reject(error.response);
  },
);

// Export default methods.
export default {
  get: axiosInstance.get,
  post: axiosInstance.post,
  put: axiosInstance.put,
  patch: axiosInstance.patch,
  delete: axiosInstance.delete,
};
